// S-9 `/onboarding/intro` — короткое знакомство перед анкетой.
// Source: docs/specs/sprint-3/stage-4-screens.md §5.9.

import { useLocation } from 'wouter';
import type { LucideIcon } from 'lucide-react';
import { ArrowRight, ClipboardList, Clock3, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';

type IntroPoint = {
  icon: LucideIcon;
  title: string;
  text: string;
};

const points: IntroPoint[] = [
  {
    icon: Clock3,
    title: 'Около 3 минут',
    text: 'Пять коротких шагов: базовые данные, образ жизни, самочувствие, история и лекарства.',
  },
  {
    icon: Sparkles,
    title: 'Персональные подсказки',
    text: 'Ответы помогут AI учитывать контекст, а не смотреть на анализы в отрыве от человека.',
  },
  {
    icon: ShieldCheck,
    title: 'Можно вернуться назад',
    text: 'Во время анкеты предыдущие ответы сохраняются в текущей сессии.',
  },
];

export function OnboardingIntro() {
  const [, setLocation] = useLocation();

  return (
    <MobileFrame>
      <ScreenHeader title="Знакомство" />

      <main className="flex flex-1 flex-col overflow-y-auto px-6 pb-8 pt-4">
        <div className="mb-7 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-surface bg-primary text-primary-foreground">
            <ClipboardList className="h-8 w-8" />
          </div>
          <h1 className="mb-3 text-[26px] font-bold leading-tight">Познакомимся с вашим здоровьем</h1>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Сейчас будет короткая анкета. Она нужна, чтобы будущие рекомендации были точнее и полезнее.
          </p>
        </div>

        <div className="space-y-3">
          {points.map(({ icon: Icon, title, text }) => (
            <div key={title} className="flex gap-3 rounded-control border border-border bg-card p-4">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary-text">
                <Icon className="h-4 w-4" />
              </div>
              <div>
                <h2 className="mb-1 text-sm font-semibold">{title}</h2>
                <p className="text-hl-small leading-relaxed text-muted-foreground">{text}</p>
              </div>
            </div>
          ))}
        </div>

        {/* mt-auto в flex-col: прижимаем кнопку + disclaimer к низу видимого viewport.
            MobileFrame использует h-dvh, поэтому "низ" — это реальная видимая область
            (не 100vh, который у Chrome iOS больше viewport). */}
        <div className="mt-auto pt-8">
          <Button
            type="button"
            className="h-11 w-full text-sm font-semibold"
            onClick={() => setLocation('/survey/step-1')}
          >
            Начать анкету
            <ArrowRight className="h-4 w-4" />
          </Button>
          <p className="mt-3 text-center text-hl-small leading-snug text-muted-foreground">
            HealthLab не ставит диагнозы и не заменяет консультацию врача.
          </p>
        </div>
      </main>
    </MobileFrame>
  );
}
